import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Box, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  CardMedia, 
  Button, 
  Container, 
  TextField, 
  InputAdornment,
  IconButton,
  Divider,
  Chip,
  Paper,
  Tooltip,
  Badge,
  Stack,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { 
  Search as SearchIcon, 
  FilterList as FilterIcon,
  BookmarkBorder as BookmarkIcon,
  Bookmark as BookmarkedIcon,
  ArrowForward as ArrowForwardIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import DashboardLayout from '../components/layout/DashboardLayout';
import bannerTemplates from '../data/bannerTemplates';
import { useLayoutContext } from '../context/LayoutContext';
import TemplatePreview from '../components/banner-designer/TemplatePreview';

const categories = [
  { id: 'all', name: 'All Templates' },
  { id: 'professional', name: 'Professional' },
  { id: 'creative', name: 'Creative' },
  { id: 'minimal', name: 'Minimal' },
  { id: 'corporate', name: 'Corporate' },
  { id: 'personal', name: 'Personal' }
];

const BannerTemplates = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { templateId } = useParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [filteredTemplates, setFilteredTemplates] = useState(bannerTemplates);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  // Filter templates based on search query and category
  useEffect(() => {
    let filtered = bannerTemplates;
    
    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(template => 
        template.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    // Filter by category (if not 'all')
    if (selectedCategory !== 'all') {
      // This is a placeholder - in a real app, templates would have category properties
      // For now, we'll just filter based on name containing the category
      filtered = filtered.filter(template => 
        template.name.toLowerCase().includes(selectedCategory.toLowerCase())
      );
    }
    
    setFilteredTemplates(filtered);
  }, [searchQuery, selectedCategory]);

  // Check if a specific template ID was provided in the URL
  useEffect(() => {
    if (templateId) {
      const template = bannerTemplates.find(t => t.id === templateId);
      if (template) {
        setSelectedTemplate(template);
      }
    }
  }, [templateId]);
  
  // If a template is selected via URL, navigate to the designer
  useEffect(() => {
    if (selectedTemplate) {
      navigate(`/banner-designer/${selectedTemplate.id}`, { replace: true });
    }
  }, [selectedTemplate, navigate]);

  // Toggle favorite status
  const toggleFavorite = (templateId, event) => {
    event.preventDefault();
    event.stopPropagation();
    
    setFavorites(prev => {
      if (prev.includes(templateId)) {
        return prev.filter(id => id !== templateId);
      } else {
        return [...prev, templateId];
      }
    });
  };

  // Handle preview
  const handlePreview = (template, event) => {
    event.preventDefault();
    event.stopPropagation();
    setPreviewTemplate(template);
    setShowPreview(true);
  };

  // Close preview
  const handleClosePreview = () => {
    setShowPreview(false);
    setPreviewTemplate(null);
  };

  return (
    <>
      <Helmet>
        <title>Banner Templates | Instation</title>
        <meta name="description" content="Choose from our collection of professional banner templates to create stunning LinkedIn banners." />
      </Helmet>
      
      <DashboardLayout>
        <Container maxWidth="xl" className="page-container">
          <Box className="content-container">
            <Paper 
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 2,
                backgroundColor: 'white',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                mb: 3,
                border: '1px solid rgba(0, 0, 0, 0.08)'
              }}
            >
              {/* Header */}
              <Box sx={{ 
                mb: 3, 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' }, 
                justifyContent: 'space-between', 
                alignItems: { xs: 'flex-start', sm: 'center' },
                gap: 2
              }}>
                <Typography 
                  variant="h6" 
                  className="component-heading"
                  sx={{ mb: 0 }}
                >
                  Banner Templates
                </Typography>
              </Box>

              {/* Search and Filter */}
              <Paper 
                elevation={0} 
                className="content-paper"
                sx={{ 
                  mb: 3, 
                  p: 2, 
                  borderRadius: 2,
                  border: '1px solid rgba(0, 0, 0, 0.08)',
                  display: 'flex', 
                  flexDirection: { xs: 'column', md: 'row' },
                  gap: 2, 
                  flexWrap: 'wrap',
                  bgcolor: 'background.paper'
                }}
              >
                <TextField
                  placeholder="Search templates..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  size="small"
                  sx={{ 
                    flexGrow: 1, 
                    minWidth: { xs: '100%', md: 300 },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: 'var(--primary-color)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'var(--primary-color)',
                      }
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1, 
                  flexWrap: 'wrap',
                  width: { xs: '100%', md: 'auto' }
                }}>
                  <IconButton size="small">
                    <FilterIcon fontSize="small" />
                  </IconButton>
                  <Divider orientation="vertical" flexItem />
                  {categories.map(category => (
                    <Chip 
                      key={category.id}
                      label={category.name}
                      onClick={() => setSelectedCategory(category.id)}
                      color={selectedCategory === category.id ? "primary" : "default"}
                      variant={selectedCategory === category.id ? "filled" : "outlined"}
                      sx={{ 
                        fontWeight: selectedCategory === category.id ? 500 : 400,
                        borderRadius: 2,
                        px: 0.75,
                        py: 0.5,
                        height: 32,
                        fontSize: '0.85rem',
                        '& .MuiChip-label': {
                          padding: '0 8px'
                        },
                        ...(selectedCategory === category.id ? {
                          bgcolor: 'var(--primary-color)',
                          color: 'white',
                          '&:hover': { 
                            bgcolor: 'var(--primary-dark)' 
                          }
                        } : {
                          '&:hover': { 
                            borderColor: 'var(--primary-color)',
                            color: 'var(--primary-color)'
                          }
                        })
                      }}
                    />
                  ))}
                </Box>
              </Paper>

              {/* Templates Grid */}
              <Grid container spacing={3} sx={{ mb: 3 }}>
                {filteredTemplates.map((template) => (
                  <Grid item xs={12} sm={6} key={template.id}>
                    <Card 
                      sx={{ 
                        height: '100%', 
                        display: 'flex', 
                        flexDirection: 'column',
                        borderRadius: 2,
                        overflow: 'hidden',
                        position: 'relative',
                        transition: 'all 0.2s ease',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                        border: '1px solid rgba(0, 0, 0, 0.08)',
                        '&:hover': {
                          transform: 'translateY(-3px)',
                          boxShadow: '0 8px 15px rgba(0,0,0,0.08)'
                        }
                      }}
                    >
                      <Link 
                        to={`/banner-designer/${template.id}`} 
                        style={{ textDecoration: 'none' }}
                      >
                        <Box sx={{ 
                          position: 'relative',
                            borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                            cursor: 'pointer',
                            bgcolor: '#f5f5f5',
                          aspectRatio: '1584/396',
                          overflow: 'hidden'
                        }}>
                          <TemplatePreview 
                            template={template}
                            width="100%"
                        />
                        </Box>
                        
                        {/* Bookmark button */}
                        <IconButton 
                          size="small"
                          sx={{ 
                            position: 'absolute', 
                            top: 8, 
                            right: 8, 
                            bgcolor: 'rgba(255,255,255,0.9)',
                            boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
                            '&:hover': { bgcolor: 'rgba(255,255,255,1)' }
                          }}
                          onClick={(e) => toggleFavorite(template.id, e)}
                        >
                          {favorites.includes(template.id) ? (
                            <BookmarkedIcon fontSize="small" sx={{ color: 'var(--primary-color)' }} />
                          ) : (
                            <BookmarkIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Link>
                      <CardContent sx={{ 
                        flexGrow: 1, 
                        display: 'flex', 
                        flexDirection: 'column',
                        p: 2
                      }}>
                        <Typography 
                          variant="subtitle1" 
                          className="section-title"
                          style={{ fontWeight: 500, color: 'black' }}
                          gutterBottom
                        >
                          {template.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          Professional LinkedIn banner template
                        </Typography>
                        <Box sx={{ mt: 'auto', pt: 2 }}>
                          <Stack direction="row" spacing={1}>
                            <Button 
                              variant="outlined" 
                              fullWidth
                              startIcon={<VisibilityIcon />}
                              onClick={(e) => handlePreview(template, e)}
                              sx={{ 
                                py: 0.75,
                                borderRadius: 2,
                                textTransform: 'none',
                                fontWeight: 500,
                                borderColor: 'var(--primary-color)',
                                color: 'var(--primary-color)',
                                '&:hover': { 
                                  borderColor: 'var(--primary-dark)',
                                  bgcolor: 'rgba(72, 144, 254, 0.04)'
                                }
                              }}
                            >
                              Preview
                            </Button>
                            <Button 
                              variant="contained" 
                              fullWidth
                              component={Link}
                              to={`/banner-designer/${template.id}`}
                              endIcon={<ArrowForwardIcon />}
                              sx={{ 
                                py: 0.75,
                                borderRadius: 2,
                                textTransform: 'none',
                                fontWeight: 500,
                                bgcolor: 'var(--primary-color)',
                                '&:hover': { bgcolor: 'var(--primary-dark)' }
                              }}
                            >
                              Use
                            </Button>
                          </Stack>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
                
                {filteredTemplates.length === 0 && (
                  <Box sx={{ 
                    p: 4, 
                    width: '100%', 
                    textAlign: 'center',
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    border: '1px dashed rgba(0, 0, 0, 0.1)',
                    mt: 2
                  }}>
                    <Typography variant="h6" color="text.secondary">
                      No templates found matching your criteria
                    </Typography>
                    <Button 
                      variant="outlined" 
                      sx={{ 
                        mt: 2, 
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 500,
                        borderColor: 'var(--primary-color)',
                        color: 'var(--primary-color)',
                        '&:hover': { 
                          borderColor: 'var(--primary-dark)',
                          bgcolor: 'rgba(72, 144, 254, 0.04)'
                        }
                      }}
                      onClick={() => {
                        setSearchQuery('');
                        setSelectedCategory('all');
                      }}
                    >
                      Clear Filters
                    </Button>
                  </Box>
                )}
              </Grid>
              
              {/* Footer */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                py: 2,
                borderTop: '1px solid rgba(0, 0, 0, 0.08)',
                mt: 'auto'
              }}>
                <Typography variant="body2" color="text.secondary">
                  {filteredTemplates.length} templates available
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Need a custom template? <Link to="/contact" style={{ color: 'var(--primary-color)', textDecoration: 'none', fontWeight: 500 }}>Contact us</Link>
                </Typography>
              </Box>
            </Paper>
          </Box>
        </Container>

        {/* Preview Dialog */}
        <Dialog
          open={showPreview}
          onClose={handleClosePreview}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              maxHeight: '90vh'
            }
          }}
        >
          <DialogTitle sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            pb: 2,
            borderBottom: '1px solid rgba(0, 0, 0, 0.08)'
          }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {previewTemplate?.name}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                LinkedIn Banner Preview • 1584 × 396px
              </Typography>
            </Box>
            <IconButton onClick={handleClosePreview} sx={{ color: 'text.secondary' }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          
          <DialogContent sx={{ p: 3 }}>
            {previewTemplate && (
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: 300,
                bgcolor: '#f5f5f5',
                borderRadius: 2,
                p: 2
              }}>
                <TemplatePreview 
                  template={previewTemplate} 
                  width={792}
                />
              </Box>
            )}
          </DialogContent>
          
          <DialogActions sx={{ 
            p: 3, 
            pt: 2,
            borderTop: '1px solid rgba(0, 0, 0, 0.08)',
            gap: 2
          }}>
            <Button 
              onClick={handleClosePreview}
              variant="outlined"
              sx={{ 
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500
              }}
            >
              Close
            </Button>
            <Button 
              variant="contained"
              component={Link}
              to={`/banner-designer/${previewTemplate?.id}`}
              endIcon={<ArrowForwardIcon />}
              sx={{ 
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                bgcolor: 'var(--primary-color)',
                '&:hover': { bgcolor: 'var(--primary-dark)' }
              }}
            >
              Use This Template
            </Button>
          </DialogActions>
        </Dialog>
      </DashboardLayout>
    </>
  );
};

export default BannerTemplates; 